import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useContentSchema } from '../../hooks/useContentSchema';
import { getInitialValues } from '../../hooks/useFillFormForUpdate';
import { useSubmitContent } from '../../hooks/useSubmitContent';
import { useHostContext } from 'src/core/hooks/useHostContext';
import { DataHubService } from 'src/core/services/datahub';
import { ViewContentCreateSchema } from 'src/core/services/datahub/schemas/content/viewContentCreateSchema';
import { ViewContentEditSchema } from 'src/core/services/datahub/schemas/content/viewContentEditSchema';
import { updateTemporaryId } from 'src/core/services/upload/utils';
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from 'src/shared/components/drawer';
import { DrawerHelper } from 'src/shared/components/drawer/helper';
import { usePreventLeave } from 'src/shared/components/preventLeaveModal/hooks/usePreventLeave';
import { PreventLeaveModal } from 'src/shared/components/preventLeaveModal/preventLeaveModal';
import { Routes } from 'src/shared/constants/routes';
import { ObjectHelper } from 'src/shared/helpers/object';
import { Body } from './body';
import { Footer } from './footer';
import { Header } from './header';
export function ManageContent() {
    const [opened, setOpened] = useState(true);
    const { arrivedFrom } = useHostContext();
    const [isCreateAnotherContentEnabled, setIsCreateAnotherContentEnabled] = useState(false);
    const [lastContentCreatedQueryParams, setLastContentCreatedQueryParam] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { pageId } = useParams();
    const isUpdate = Boolean(pageId) && pageId !== 'new';
    useEffect(function effectDispatchEvent() {
        const schema = new (isUpdate ? ViewContentEditSchema : ViewContentCreateSchema)({
            pageType: 'CONTENT',
            arrivedFrom: arrivedFrom || 'DIRECT'
        });
        DataHubService.sendEvent({ schema });
    }, [arrivedFrom, isUpdate]);
    const form = useForm({
        mode: 'onChange',
        resolver: valibotResolver(useContentSchema()),
        defaultValues: getInitialValues()
    });
    const { isValid, dirtyFields } = form.formState;
    const { submit, handleSubmitButtonClick, isSubmitting, submitAction } = useSubmitContent({
        isUpdate,
        resetForm: form.reset,
        isCreateAnotherContentEnabled,
        setLastContentCreatedQueryParam,
        onSubmitSuccess: () => setOpened(false)
    });
    const handleLeaveConfirmation = useCallback(() => {
        setOpened(false);
        let searchParams = location.search;
        if (lastContentCreatedQueryParams) {
            setLastContentCreatedQueryParam(null);
            searchParams = lastContentCreatedQueryParams;
        }
        DrawerHelper.listenerOnClose(() => {
            updateTemporaryId(null);
            navigate({ pathname: Routes.ListModules, search: searchParams });
        });
    }, [navigate, location.search, lastContentCreatedQueryParams]);
    const { handleLeave, preventLeaveModalProps } = usePreventLeave({
        onLeaveConfirmation: handleLeaveConfirmation,
        dirtyFields
    });
    return (_jsx(FormProvider, { ...form, children: _jsx("form", { id: "content-form", onSubmit: (e) => submit(e, form.handleSubmit), className: "h-full", children: _jsxs(Drawer, { opened: opened, type: "bottomsheet", onCloseDrawer: handleLeave, children: [_jsx(DrawerHeader, { children: _jsx(Header, { isUpdate: isUpdate }) }), _jsxs(DrawerBody, { className: "overflow-y-scroll", children: [_jsx(Body, { isRedirectingToNewContentForm: isCreateAnotherContentEnabled && isSubmitting }), _jsx(PreventLeaveModal, { ...preventLeaveModalProps })] }), _jsx(DrawerFooter, { children: _jsx(Footer, { isUpdate: isUpdate, onSubmitClick: handleSubmitButtonClick, isSubmitDisabled: ObjectHelper.isEmpty(dirtyFields) || !isValid || isSubmitting, isSubmitting: isSubmitting && submitAction === 'submit', isPublishing: isSubmitting && submitAction === 'publish', setIsCreateAnotherContentEnabled: () => setIsCreateAnotherContentEnabled((prev) => !prev), isCreateAnotherContentEnabled: isCreateAnotherContentEnabled }) })] }) }) }));
}
